import { useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconArrowRight, IconEuro } from '@iwoca/icons';
import { currencyToNumber, Input, Message } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { FormProvider, useForm } from 'react-hook-form';

import MarketingOptIn from './../MarketingOptIn/MarketingOptIn';
import styles from './GetStartedDE.module.css';
import { schema } from './GetStartedDE.schema';
import LoanDurationDE, { LOAN_DURATION_OPTIONS_DE } from './LoanDurationDE';
import LoanPurposeDE, { LOAN_PURPOSE_OPTIONS_DE } from './LoanPurposeDE';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
import { getInitialApplicationBodyDE } from '../../../helpers/getApplicationBody';
import { getLatestStepDE } from '../../../helpers/getLatestStep/getLatestStepDE';
import { useSignupParams } from '../../../hooks/useSignupParams';
import { useSignupRouterDE } from '../../../hooks/useSignupRouter/useSignupRouterDE';
import { TGetStateResponseDE } from '../../../lapiOverrideTypesDE';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { logErrorToDataLayer } from '../../../utils/googleDataLayer';
import logger from '../../../utils/logger';
import { Button } from '../../Button/Button';
import { normaliseStepDataDE } from '../../helpers/stateNormalisation';
import { GET_STARTED_STEP_ID } from '../../helpers/steps';
import { useSubmitGetStarted } from '../submitGetStarted';
import DetailedPurposeDE from './LoanPurposeDetailsDE';

export type TGetStartedDataDE = {
  email: string;
  amount: string;
  loanDuration?: (typeof LOAN_DURATION_OPTIONS_DE)[number]['value'];
  loanPurpose: (typeof LOAN_PURPOSE_OPTIONS_DE)[number]['value'];
  loanPurposeDetails?: string;
  marketingOptIn?: boolean;
};

type TProps = {
  handleStateUpdateError: (error: Error) => void;
};

const GetStartedDE = ({ handleStateUpdateError }: TProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { affiliateTag, referredAmount } = useSignupParams();
  const { navigateSignupDE } = useSignupRouterDE();
  const { submitGetStarted, postGoogleTrackingSuccess } = useSubmitGetStarted();

  const { state } = useGetStateQuery<TGetStateResponseDE>();
  const { application } = useGetApplicationQuery();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepDataDE(data)[GET_STARTED_STEP_ID],
  );

  const { t } = useLingui();

  const methods = useForm<TGetStartedDataDE>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...normalisedState,
      amount:
        referredAmount ||
        application?.requested_products?.[0].amount?.toString(),
    },
  });

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Get started',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  useEffect(() => {
    tracking.ampli.queryParamLoaded({
      query_param: 'amount',
      query_value: referredAmount ?? undefined,
      query_exists: Boolean(referredAmount),
      ...getAmplitudeDefaultEventProperties(),
    });
  }, [referredAmount]);

  useEffect(() => {
    const navigateToLatestStep = async () => {
      if (!isSubmitted || !state?.state_key || !application) return;

      const newStep = getLatestStepDE(state, application);
      setIsSubmitting(true);
      await navigateSignupDE(newStep, state.state_key);
    };

    void navigateToLatestStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted, state, application]);

  useEffect(() => {
    if (normalisedState?.email) {
      setValue('email', normalisedState.email, {
        shouldValidate: true,
      });
    }

    if (normalisedState?.marketingOptIn) {
      setValue('marketingOptIn', normalisedState?.marketingOptIn, {
        shouldValidate: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalisedState]);

  useEffect(() => {
    const amount = application?.requested_products?.[0].amount;
    if (amount) {
      setValue('amount', amount.toString(), {
        shouldValidate: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application]);

  const formatCurrency = useCallback(() => {
    const amount = watch('amount')?.replace(/[^0-9]+/g, '');
    if (!amount) return '';

    return parseInt(amount).toLocaleString('de');
  }, [watch]);

  const onSubmit = async (values: TGetStartedDataDE) => {
    try {
      setError(false);
      setIsSubmitting(true);

      tracking.ampli.applicationStarted({
        requestedAmount: currencyToNumber(values.amount, {
          currencySymbol: true,
          locale: 'de-DE',
          minorUnits: false,
        }),
        loanDuration: values.loanDuration,
        loanPurpose: values.loanPurpose,
        ...(referredAmount && {
          amount_query_matches: values.amount === referredAmount,
        }),
        ...getAmplitudeDefaultEventProperties(),
      });

      const applicationBody = getInitialApplicationBodyDE(affiliateTag, values);

      const hadCompleteApplication = Boolean(
        application?.requested_products?.[0].amount &&
          application?.requested_products?.[0].purpose,
      );

      await submitGetStarted({
        applicationBody,
        hadCompleteApplication,
        values,
      });

      postGoogleTrackingSuccess(values);

      // redirect will happen in above use effect when application and state refetched.
      setIsSubmitted(true);
    } catch (error: unknown) {
      logErrorToDataLayer('Step Zero - Form submission error');

      if (error instanceof Error) {
        setError(true);
        handleStateUpdateError(error);

        if (error instanceof Error) {
          logger.error('Failed to start application', {
            error: error.message,
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.heading}>
        <Trans>Let’s get started</Trans>
      </h1>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.formFields}>
            <Input
              {...register('email')}
              wrapperClassName={`${styles.formField} fs-mask`}
              disabled={Boolean(normalisedState?.email)}
              label={t`What is your business email address?`}
              placeholder={t`firstname@example.com`}
              error={errors.email?.message}
            />
            <Input
              {...register('amount')}
              value={formatCurrency()}
              wrapperClassName={styles.formField}
              iconRight={<IconEuro />}
              label={t`How much would you like?`}
              placeholder={t`Up to £500,000`}
              error={errors.amount?.message}
            />
            <LoanDurationDE />
            <LoanPurposeDE />
            <DetailedPurposeDE />
            <p
              className={styles.privacyPolicySection}
              data-testid="privacy-policy-section"
            >
              <small>
                Hiermit willige ich in die Verarbeitung meiner personenbezogenen
                Daten gemäß unserer aktuellen{' '}
                <a
                  href="https://www.iwoca.de/datenschutzhinweis/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Datenschutzerklärung
                </a>{' '}
                der iwoca Deutschland GmbH ein. Die erteilte Einwilligung kann
                jederzeit widerrufen werden. Mit den{' '}
                <a
                  href="https://www.iwoca.de/allgemeine_geschaeftsbedingungen/"
                  target="_blank"
                  rel="noreferrer"
                >
                  AGB
                </a>{' '}
                der iwoca Deutschland GmbH erkläre ich mich einverstanden.
              </small>
            </p>
            <MarketingOptIn />
          </div>
          <Button
            className={styles.submit}
            loading={isSubmitting}
            type="submit"
          >
            <Trans>Get started</Trans>
            <IconArrowRight />
          </Button>
        </form>
      </FormProvider>
      {error && (
        <Message
          className={styles.error}
          type="error"
          // TODO: needs a translation
          message="We encountered an error while creating your application"
        />
      )}
    </div>
  );
};

export default GetStartedDE;
